// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Per-agent track cards: one card per agent, with the agent's slots inline,
 * registered renderer output, and a run button.
 *
 * Findings rendering is dispatched through the renderer-component registry
 * (`agent.rendererIds` → workspace `RendererDefinition`s → registered
 * `aggregate`-mode component). When an agent has no renderers configured, a
 * default `srm-findings` renderer is synthesized binding the `findings` slot
 * to `findingsDatasetKey(agentId)`.
 */

import { type FC, useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { AlertTriangle, Braces, FileText, Plus, Trash2, Play, Loader2, ChevronDown, Download } from 'lucide-react'
import {
  Button,
  Tooltip,
  TooltipTrigger,
  TooltipProvider,
  DarkTooltipContent,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui'
import { toast } from 'sonner'
import type { NodeApiClient } from '@/app-shell/services'
import type { DocumentListItem } from '@/subsystems/documents'
import { deleteDocument, uploadDocument, getDocumentViewUrl, AddAdditionalDocumentDialog } from '@/subsystems/documents'
import type { Agent } from '@/subsystems/agents'
import type { RendererDefinition } from '@/app-shell/types/workspace'
import {
  getRendererComponent,
  useRendererSlotDataMulti,
} from '@/app-shell/renderer-components'
import { useAnalysisStatuses, type ParsedAnalysisStatuses } from '@/applications/shared/hooks'
import { useAuth } from '@/subsystems/workspaces'
import { trackPalette } from '../utils'
import {
  agentFindingsDatasetKeys,
  analyzedDatasetRefs,
  findingsDatasetKey,
  renderedDocuments,
  rendererDatasetKeys,
  type RendererDocRef,
} from '../utils/track'
import { RawOutputDialog } from './RawOutputDialog'

/** Dataset keys bound to the agent's aggregate-mode renderer slots. */
function agentDatasetKeys(agent: Agent): string[] {
  const keys: string[] = []
  for (const r of Object.values(agent.renderers)) {
    const def = getRendererComponent(r.component)
    if (def?.versionMode !== 'aggregate') continue
    keys.push(...rendererDatasetKeys(r))
  }
  return keys
}

interface TrackCardsProps {
  agents: Agent[]
  documents: DocumentListItem[]
  groupId: string
  apiClient: NodeApiClient
  orgId: string
  wsId: string
  onDocumentChange: () => void
  onRunAgent?: (agent: Agent) => void
  /** Agent ids whose analysis is currently in flight (tracks run independently). */
  runningAgentIds: Set<string>
  /** Per-agent error message to display in place of the findings panel. */
  agentErrors?: Record<string, string | null>
  /** When set, populated with a fn the parent can call to refetch analysis statuses. */
  refreshAnalysisStatusRef?: React.MutableRefObject<(() => void) | null>
}

export const TrackCards: FC<TrackCardsProps> = ({
  agents,
  documents,
  groupId,
  apiClient,
  orgId,
  wsId,
  onDocumentChange,
  onRunAgent,
  runningAgentIds,
  agentErrors,
  refreshAnalysisStatusRef,
}) => {
  const [addDialogAgentId, setAddDialogAgentId] = useState<string | null>(null)

  const datasetKeysByAgent = useMemo(() => {
    const map: Record<string, string[]> = {}
    for (const a of agents) map[a.agentId] = agentDatasetKeys(a)
    return map
  }, [agents])
  const agentIds = useMemo(() => agents.map(a => a.agentId), [agents])
  const { getDataset, requestRefresh } = useAnalysisStatuses({
    apiClient,
    orgId,
    workspaceId: wsId,
    agentIds,
    groupId,
  })
  useEffect(() => {
    if (!refreshAnalysisStatusRef) return
    refreshAnalysisStatusRef.current = requestRefresh
    return () => {
      refreshAnalysisStatusRef.current = null
    }
  }, [refreshAnalysisStatusRef, requestRefresh])
  const trackStatuses = useMemo(() => {
    const out: Record<string, { count: number; analyzed: boolean }> = {}
    for (const a of agents) {
      let count = 0
      let analyzed = false
      for (const dsKey of datasetKeysByAgent[a.agentId] ?? []) {
        const ds = getDataset(a.agentId, dsKey)
        if (ds.versions.size > 0) analyzed = true
        for (const c of ds.counts.values()) count += c
      }
      out[a.agentId] = { count, analyzed }
    }
    return out
  }, [agents, datasetKeysByAgent, getDataset])

  const handleAddAdditional = useCallback(
    async (agent: Agent, label: string, file: File) => {
      const slug = label
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        || 'doc'
      const existingIds = new Set([
        ...agent.slots.map((s) => s.id),
        ...documents.map((d) => d.slotId).filter((id): id is string => !!id),
      ])
      let slotId = `${agent.agentId}-${slug}`
      let n = 2
      while (existingIds.has(slotId)) {
        slotId = `${agent.agentId}-${slug}-${n++}`
      }
      try {
        await uploadDocument(apiClient, orgId, wsId, {
          file,
          name: label,
          groupId,
          slotId,
          uploadUrlBase: `/org/${orgId}/ws/${wsId}/srm/documents/upload-url`,
        })
        toast.success('Document added', { description: label })
        onDocumentChange()
      } catch (err) {
        toast.error('Add failed', {
          description: err instanceof Error ? err.message : 'Unknown error',
        })
      }
    },
    [apiClient, orgId, wsId, groupId, documents, onDocumentChange],
  )

  return (
    <TooltipProvider delayDuration={300}>
      <div className="space-y-4">
        {agents.map((agent, idx) => (
          <TrackCard
            key={agent.agentId}
            agent={agent}
            paletteIndex={idx}
            documents={documents}
            groupId={groupId}
            apiClient={apiClient}
            orgId={orgId}
            wsId={wsId}
            onDocumentChange={onDocumentChange}
            onRunAgent={onRunAgent}
            isRunning={runningAgentIds.has(agent.agentId)}
            onOpenAddDialog={() => setAddDialogAgentId(agent.agentId)}
            error={agentErrors?.[agent.agentId] ?? null}
            findingsCount={trackStatuses[agent.agentId]?.count ?? 0}
            analyzed={trackStatuses[agent.agentId]?.analyzed ?? false}
            getDataset={getDataset}
          />
        ))}
      </div>

      <AddAdditionalDocumentDialog
        open={addDialogAgentId !== null}
        onOpenChange={(open) => {
          if (!open) setAddDialogAgentId(null)
        }}
        onSubmit={async (label, file) => {
          const agent = agents.find((a) => a.agentId === addDialogAgentId)
          if (agent) await handleAddAdditional(agent, label, file)
        }}
      />
    </TooltipProvider>
  )
}

interface TrackCardProps {
  agent: Agent
  paletteIndex: number
  documents: DocumentListItem[]
  groupId: string
  apiClient: NodeApiClient
  orgId: string
  wsId: string
  onDocumentChange: () => void
  onRunAgent?: (agent: Agent) => void
  /** True while this track's own analysis is in flight. */
  isRunning: boolean
  onOpenAddDialog: () => void
  /** When set, replaces the findings panel with the error message. */
  error: string | null
  /** Total findings across this track's docs, sourced from analysis status. */
  findingsCount: number
  /** True if any document in this track has an analysis status row. */
  analyzed: boolean
  /** Per-dataset analysis status, used to fetch only the results that exist. */
  getDataset: (agentId: string, datasetKey: string) => ParsedAnalysisStatuses
}

const TrackCard: FC<TrackCardProps> = ({
  agent,
  paletteIndex,
  documents,
  groupId,
  apiClient,
  orgId,
  wsId,
  onDocumentChange,
  onRunAgent,
  isRunning,
  onOpenAddDialog,
  error,
  findingsCount,
  analyzed,
  getDataset,
}) => {
  const { isGlobalAdmin } = useAuth()
  const [expanded, setExpanded] = useState(false)
  const [rawOpen, setRawOpen] = useState(false)
  const isOpen = expanded
  const palette = trackPalette(paletteIndex)
  const Icon = palette.Icon
  const datasetKeys = useMemo(() => agentFindingsDatasetKeys(agent), [agent])

  const namedSlotIds = useMemo(() => new Set(agent.slots.map((s) => s.id)), [agent.slots])
  const namedTrackDocs = useMemo(
    () =>
      agent.slots.map((slot) => ({
        slotId: slot.id,
        label: slot.label,
        doc: documents.find((d) => d.slotId === slot.id) ?? null,
      })),
    [agent.slots, documents],
  )
  const adHocTrackDocs = useMemo(
    () =>
      documents
        .filter(
          (d) =>
            d.slotId
            && d.slotId.startsWith(`${agent.agentId}-`)
            && !namedSlotIds.has(d.slotId),
        )
        .map((d) => ({
          slotId: d.slotId as string,
          label: d.name || d.latestFilename,
          doc: d,
        })),
    [documents, agent.agentId, namedSlotIds],
  )
  const trackDocs = useMemo(
    () => [...namedTrackDocs, ...adHocTrackDocs],
    [namedTrackDocs, adHocTrackDocs],
  )
  const agentDocs = useMemo(
    () => trackDocs.filter((td) => td.doc !== null).map((td) => td.doc as DocumentListItem),
    [trackDocs],
  )
  const hasUploadedDocs = agentDocs.length > 0
  const rawDatasetRefs = useMemo(
    () => analyzedDatasetRefs(agentDocs, datasetKeys, agent.agentId, getDataset),
    [agentDocs, datasetKeys, agent.agentId, getDataset],
  )

  // Resolve renderers: aggregate-mode renderers attached to this agent, else a
  // default synthesized one binding `findings` to the agent's findings dataset
  // key. This keeps zero-config tracks rendering.
  const resolvedRenderers: RendererDefinition[] = useMemo(() => {
    const explicit = Object.values(agent.renderers)
      .filter((r) => {
        const def = getRendererComponent(r.component)
        return def?.versionMode === 'aggregate'
      })
      // `renderers` is a map, so panel order comes from the renderer's own `order`.
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    if (explicit.length > 0) return explicit
    return [
      {
        key: `__default__${agent.agentId}`,
        label: `${agent.label} Findings`,
        component: 'srm-findings',
        slots: [
          {
            key: 'findings',
            type: 'dataset',
            datasetKey: findingsDatasetKey(agent.agentId),
          },
        ],
      },
    ]
  }, [agent.renderers, agent.agentId, agent.label])

  // A "summary" renderer (e.g. the eligibility determination) has meaningful
  // output even when there are zero findings, so the track must not be hidden on
  // an empty count. Auto-open it too, so the verdict shows without an extra click.
  const isSummaryTrack = useMemo(
    () => resolvedRenderers.some(
      (r) => getRendererComponent(r.component)?.contentKind === 'summary',
    ),
    [resolvedRenderers],
  )
  useEffect(() => {
    if (isSummaryTrack && analyzed) setExpanded(true)
  }, [isSummaryTrack, analyzed])

  const isThisAgentRunning = isRunning

  return (
    <Collapsible open={isOpen} onOpenChange={setExpanded}>
      <div data-testid={`section-${agent.agentId}`} className={`bg-card rounded-lg border ${palette.borderClass} overflow-hidden`}>
        <CollapsibleTrigger asChild>
          <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-center gap-3 min-w-0">
              <Icon className={`h-5 w-5 shrink-0 ${palette.iconClass}`} />
              <span className="font-semibold text-foreground whitespace-nowrap">{agent.label}</span>
            </div>
            <ChevronDown
              className={`h-3 w-3 ml-1 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
            <div className="flex items-center gap-2 ml-auto">
              {findingsCount > 0 && (
                <span
                  data-testid={`srm-track-findings-count-${agent.agentId}`}
                  className={`text-xs font-medium px-2 py-0.5 rounded-full border ${palette.badgeClass}`}
                >
                  {findingsCount} {findingsCount === 1 ? 'finding' : 'findings'}
                </span>
              )}
              {isGlobalAdmin && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`srm-track-raw-${agent.agentId}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        setRawOpen(true)
                      }}
                    >
                      <Braces className="h-4 w-4" />
                      <span className="ml-1.5 hidden sm:inline">Raw</span>
                    </Button>
                  </TooltipTrigger>
                  <DarkTooltipContent>View raw dataset output</DarkTooltipContent>
                </Tooltip>
              )}
              {onRunAgent && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={isRunning || !hasUploadedDocs}
                      data-testid={`srm-track-run-${agent.agentId}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        onRunAgent(agent)
                      }}
                    >
                      {isThisAgentRunning ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                      <span className="ml-1.5 hidden sm:inline">Run</span>
                    </Button>
                  </TooltipTrigger>
                  <DarkTooltipContent>
                    {hasUploadedDocs
                      ? `Run ${agent.label.toLowerCase()}`
                      : 'Upload documents first'}
                  </DarkTooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        </CollapsibleTrigger>

        {error ? (
          <div className="px-4 py-3 bg-destructive/5 border-b border-destructive/20">
            <div className="flex items-start gap-2 text-destructive">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <div className="space-y-0.5 min-w-0">
                <div className="text-sm font-medium">Analysis failed</div>
                <p className="text-sm text-destructive/90">{error}</p>
              </div>
            </div>
          </div>
        ) : !hasUploadedDocs ? (
          <div className="px-4 py-3 border-b text-sm text-muted-foreground">
            Upload documents and run review to generate findings.
          </div>
        ) : !analyzed ? (
          <div className="px-4 py-3 border-b text-sm text-muted-foreground">
            No findings — run review to generate findings.
          </div>
        ) : findingsCount === 0 && !isSummaryTrack ? (
          <div className="px-4 py-3 border-b text-sm text-muted-foreground">
            Analysis complete — no issues detected.
          </div>
        ) : (
          <CollapsibleContent>
            <div className="border-b">
              {resolvedRenderers.map((renderer) => (
                <RendererPanel
                  key={renderer.key}
                  renderer={renderer}
                  documents={renderedDocuments(renderer, agentDocs, agent.agentId, getDataset)}
                  apiClient={apiClient}
                  orgId={orgId}
                  workspaceId={wsId}
                  agentId={agent.agentId}
                  groupId={groupId}
                />
              ))}
            </div>
          </CollapsibleContent>
        )}

        {trackDocs.length > 0 && (
          <div className="divide-y">
            {trackDocs.map(({ slotId, label, doc }) => (
              <SlotRow
                key={slotId}
                slotId={slotId}
                label={label}
                document={doc}
                groupId={groupId}
                apiClient={apiClient}
                orgId={orgId}
                wsId={wsId}
                onDocumentChange={onDocumentChange}
              />
            ))}
          </div>
        )}
        {agent.allowAdditionalSlots && (
          <div className="px-4 py-2 border-t bg-muted/10">
            <Button
              variant="ghost"
              size="sm"
              onClick={onOpenAddDialog}
              data-testid={`srm-add-additional-${agent.agentId}`}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add additional document
            </Button>
          </div>
        )}
      </div>
      {isGlobalAdmin && (
        <RawOutputDialog
          open={rawOpen}
          onOpenChange={setRawOpen}
          agentLabel={agent.label}
          agentId={agent.agentId}
          datasetRefs={rawDatasetRefs}
          apiClient={apiClient}
          orgId={orgId}
          wsId={wsId}
          groupId={groupId}
        />
      )}
    </Collapsible>
  )
}

interface RendererPanelProps {
  renderer: RendererDefinition
  /** Documents with stored results for this renderer, already resolved from analysis status. */
  documents: RendererDocRef[]
  apiClient: NodeApiClient
  orgId: string
  workspaceId: string
  /** Agent owning this renderer — dataset keys are only unique within it. */
  agentId: string
  groupId: string
}

const RendererPanel: FC<RendererPanelProps> = ({
  renderer,
  documents,
  apiClient,
  orgId,
  workspaceId,
  agentId,
  groupId,
}) => {
  const componentDef = getRendererComponent(renderer.component)

  const { documentSlotData, hasResults, loading } = useRendererSlotDataMulti({
    apiClient,
    renderer,
    documents,
    orgId,
    workspaceId,
    agentId,
    groupId,
    enabled: documents.length > 0 && !!componentDef,
  })

  if (!componentDef) {
    return (
      <div className="px-4 py-3 text-sm text-muted-foreground">
        Renderer "{renderer.component ?? '(unset)'}" is not registered.
      </div>
    )
  }

  if (documents.length === 0) {
    return (
      <div className="px-4 py-3 text-sm text-muted-foreground">
        Not reviewed yet — run review to generate findings.
      </div>
    )
  }

  if (loading) {
    return (
      <div className="px-4 py-3 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading {renderer.label.toLowerCase()}...
      </div>
    )
  }

  if (!hasResults) {
    return (
      <div className="px-4 py-3 text-sm text-muted-foreground">
        No findings — run review to generate findings.
      </div>
    )
  }

  const Component = componentDef.Component
  return (
    <Component
      renderer={renderer}
      // For aggregate mode, no single documentId/version applies. Pass the
      // first doc as a reasonable default for components that read these
      // fields (most aggregate renderers will only read documentSlotData).
      documentId={documents[0]?.documentId ?? ''}
      orgId={orgId}
      workspaceId={workspaceId}
      agentId={agentId}
      apiClient={apiClient}
      documentSlotData={documentSlotData}
    />
  )
}

interface SlotRowProps {
  slotId: string
  label: string
  document: DocumentListItem | null
  groupId: string
  apiClient: NodeApiClient
  orgId: string
  wsId: string
  onDocumentChange: () => void
}

const SlotRow: FC<SlotRowProps> = ({
  slotId,
  label,
  document,
  groupId,
  apiClient,
  orgId,
  wsId,
  onDocumentChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const handleFileSelect = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      e.target.value = ''
      setIsUploading(true)
      try {
        if (document) {
          await deleteDocument(apiClient, orgId, wsId, document.documentId, groupId)
        }
        await uploadDocument(apiClient, orgId, wsId, {
          file,
          name: file.name,
          groupId,
          slotId,
          uploadUrlBase: `/org/${orgId}/ws/${wsId}/srm/documents/upload-url`,
        })
        toast.success(document ? 'Document replaced' : 'Document uploaded', {
          description: file.name,
        })
        onDocumentChange()
      } catch (err) {
        toast.error('Upload failed', {
          description: err instanceof Error ? err.message : 'Unknown error',
        })
      } finally {
        setIsUploading(false)
      }
    },
    [apiClient, orgId, wsId, document, groupId, slotId, onDocumentChange],
  )

  const handleRemove = useCallback(async () => {
    if (!document) return
    setIsDeleting(true)
    try {
      await deleteDocument(apiClient, orgId, wsId, document.documentId, groupId)
      toast.success('Document removed', { description: document.latestFilename })
      onDocumentChange()
    } catch (err) {
      toast.error('Remove failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    } finally {
      setIsDeleting(false)
      setDeleteDialogOpen(false)
    }
  }, [apiClient, orgId, wsId, document, groupId, onDocumentChange])

  const handleDownload = useCallback(async () => {
    if (!document) return
    try {
      const url = await getDocumentViewUrl(apiClient, orgId, wsId, document.documentId, undefined, groupId)
      const link = window.document.createElement('a')
      link.href = url
      link.download = document.latestFilename
      window.document.body.appendChild(link)
      link.click()
      window.document.body.removeChild(link)
    } catch (err) {
      toast.error('Download failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    }
  }, [apiClient, orgId, wsId, document, groupId])

  const triggerFileSelect = () => fileInputRef.current?.click()

  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-3 hover:bg-muted/20">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {document ? (
          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
        ) : (
          <div className="h-4 w-4 rounded-full border-2 border-dashed border-muted-foreground/40 shrink-0" />
        )}
        <div className="min-w-0">
          <div className={`text-sm font-medium truncate ${document ? 'text-foreground' : 'text-muted-foreground'}`}>
            {label}
          </div>
          {document && <div className="text-xs text-muted-foreground truncate">{document.latestFilename}</div>}
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0 ml-auto">
        {document && (
          <span className="text-xs text-muted-foreground">{formatFileSize(document.latestSizeBytes)}</span>
        )}
        {document && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleDownload}
                disabled={isUploading || isDeleting}
                aria-label="Download document"
              >
                <Download className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <DarkTooltipContent>Download</DarkTooltipContent>
          </Tooltip>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={triggerFileSelect}
              disabled={isUploading || isDeleting}
              aria-label={document ? 'Replace document' : 'Upload document'}
            >
              {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            </Button>
          </TooltipTrigger>
          <DarkTooltipContent>{document ? 'Replace' : 'Upload'}</DarkTooltipContent>
        </Tooltip>
        {document && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive hover:text-destructive"
                onClick={() => setDeleteDialogOpen(true)}
                disabled={isUploading || isDeleting}
                aria-label="Delete document"
              >
                {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              </Button>
            </TooltipTrigger>
            <DarkTooltipContent>Remove</DarkTooltipContent>
          </Tooltip>
        )}
      </div>

      <input ref={fileInputRef} type="file" className="hidden" accept=".pdf,.doc,.docx" onChange={handleFileSelect} data-testid={`srm-slot-file-${slotId}`} />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove document?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove "{document?.latestFilename}" from this request.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemove} disabled={isDeleting}>
              {isDeleting ? 'Removing...' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function formatFileSize(bytes: number | undefined): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
